import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { ConnectInterventionModal, Dropdown, InterventionModal, SnoozeModal, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useAppendQuestionResultSnoozeMutation } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/QuestionResultActionSelector/hooks";
import "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/QuestionResultActionSelector/QuestionResultActionSelector.scss";
import { useAppointmentDealer, useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { parseQuestionResultAttachments } from "modules/AppointmentDetails/utils";
import { canWriteBackCustomerData, generateDMSNr } from "utils/appointmentUtils";

import type { ConnectToInterventionPayload } from "components";
import type { Appointment, Checklist, QuestionResult, Snooze } from "models";
import type { QuestionResultData } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/types";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type ActionModalSelectorProps = {
  appointment: Appointment;
  questionResult: QuestionResult;
  checklist?: Checklist;
};

const retrieveDropdownIcon = (isQuestionResultItemConvertedOrConnected: boolean, isQuestionResultItemSnoozed: boolean) => {
  if (isQuestionResultItemConvertedOrConnected) return "link horizontal grey";
  if (isQuestionResultItemSnoozed) return "clock grey";
  return "plus grey";
};

export const QuestionResultActionSelector = ({ questionResult, checklist, appointment }: ActionModalSelectorProps) => {
  const { t } = useTranslation();

  const [snoozeQuestionResultPayload, setSnoozeQuestionResultPayload] = useState<QuestionResultData | null>(null);
  const [connectInterventionPayload, setConnectInterventionPayload] = useState<ConnectToInterventionPayload | null>(null);
  const [convertToInterventionPayload, setConvertToInterventionPayload] = useState<AddInterventionPayload | null>(null);

  const appointmentLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);
  const canCreateSnooze = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Snooze);
  const canCreateIntervention = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Interventions);
  const canUpdateIntervention = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Interventions);
  const { mutateAsync: appendSnooze, isPending: isAppendSnoozePending } = useAppendQuestionResultSnoozeMutation();

  const isQuestionResultConvertedOrConnected = appointment.interventions?.some(i => i.question_result_id === questionResult.id) ?? false;
  const hideConvertToIntervention = questionResult.question_options?.some(qo => qo.name === "hide_customer_ok_and_convert_to_intervention" && qo.value) ?? false;
  const shouldHideCustomerOkAndConvertToIntervention = isQuestionResultConvertedOrConnected || hideConvertToIntervention;
  const canConvertToIntervention =
    canCreateIntervention && appointmentLocation?.convert_checklist_to_intervention_is_enabled && !shouldHideCustomerOkAndConvertToIntervention;
  const canConnectToIntervention = canUpdateIntervention && !shouldHideCustomerOkAndConvertToIntervention;
  const canSnooze =
    canCreateSnooze &&
    appointmentLocation?.schedule_enabled &&
    (!checklist?.template || checklist.template.snooze_enabled) &&
    !isQuestionResultConvertedOrConnected &&
    (!questionResult.question_options || questionResult.question_options.find(qo => qo.name === "snooze_enabled" && qo.value));

  const handleConnectToIntervention = () => setConnectInterventionPayload({ question_result_id: questionResult.id, title: questionResult.title });

  const handleSnoozeQuestionResult = () =>
    setSnoozeQuestionResultPayload({
      title: questionResult.title,
      solution: questionResult.solution,
      description: questionResult.raw,
      question_result_id: questionResult.id,
      appointment_id: questionResult.appointment_id,
      snooze_history: questionResult.snooze_history,
      attachments: parseQuestionResultAttachments(questionResult)
    });

  const handleConvertToIntervention = () =>
    setConvertToInterventionPayload({
      title: questionResult.title || "",
      appointment_id: appointment.id,
      customer_ok: false,
      description: questionResult.mechanic_notes || questionResult.raw || "",
      price: questionResult.price,
      question_result_id: questionResult.id,
      dms_nr: generateDMSNr(),
      attachments: parseQuestionResultAttachments(questionResult)
    });

  const handleAppendSnooze = async (snooze: Snooze) => {
    if (!canCreateSnooze) return;
    await appendSnooze(snooze);
  };

  return (
    <>
      <div className="QuestionResultActionSelector">
        <Dropdown
          disabled={isQuestionResultConvertedOrConnected || questionResult.snoozed}
          className="action-dropdown"
          floating
          icon={null}
          direction="left"
          trigger={<Icon className={retrieveDropdownIcon(isQuestionResultConvertedOrConnected, questionResult.snoozed)} />}
        >
          <DropdownMenu className="dropdown-menu">
            {canConvertToIntervention && (
              <DropdownItem onClick={handleConvertToIntervention}>
                <Icon className="screwdriver wrench" />
                {t("v8_convert_to_intervention", "Convert to Intervention")}
              </DropdownItem>
            )}

            {canConnectToIntervention && (
              <DropdownItem onClick={handleConnectToIntervention}>
                <Icon className="link horizontal" />
                {t("v8_connect_to_intervention", "Connect to Intervention")}
              </DropdownItem>
            )}

            {canSnooze && (
              <DropdownItem onClick={handleSnoozeQuestionResult}>
                <Icon className="clock" />
                {t("v8_snooze", "Snooze")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <InterventionModal
        appointmentId={appointment.id}
        carId={appointment.car_id}
        location={appointmentLocation}
        dealer={appointmentDealer}
        open={!!convertToInterventionPayload}
        intervention={convertToInterventionPayload}
        onClose={() => setConvertToInterventionPayload(null)}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />
      {snoozeQuestionResultPayload && (
        <SnoozeModal
          dealer={appointmentDealer}
          location={appointmentLocation}
          size="large"
          open={!!snoozeQuestionResultPayload}
          onClose={() => setSnoozeQuestionResultPayload(null)}
          closeOnDimmerClick
          data={snoozeQuestionResultPayload}
          onAppendSnooze={handleAppendSnooze}
          isAppendSnoozeLoading={isAppendSnoozePending}
        />
      )}
      <ConnectInterventionModal
        open={Boolean(connectInterventionPayload)}
        appointment={appointment}
        checklistName={checklist?.name ?? ""}
        closeOnDimmerClick
        connectToInterventionPayload={connectInterventionPayload}
        onClose={() => setConnectInterventionPayload(null)}
      />
    </>
  );
};
